<template>
  <div class="dashboards">
    <!-- ============ Picker (no dashboard open) ============ -->
    <template v-if="!current">
      <div class="toolbar">
        <h2 class="picker-title">Dashboards</h2>
        <div class="toolbar-spacer" />
        <LgButton variant="primary" size="sm" @click="newOpen = true">
          <template #icon><Plus :size="14" aria-hidden="true" /></template>
          New dashboard
        </LgButton>
      </div>

      <div class="picker-body">
        <div v-if="listError" class="picker-state">
          <GoblinIcon variant="error" :size="48" />
          <p>Couldn’t load dashboards.</p>
          <LgButton size="sm" variant="ghost" @click="loadList">Retry</LgButton>
        </div>

        <div v-else-if="listLoading && !list.length" class="picker-state">
          <p>Loading dashboards…</p>
        </div>

        <div v-else-if="!list.length" class="picker-state">
          <GoblinIcon variant="empty" :size="64" />
          <p class="state-title">No dashboards yet</p>
          <p class="state-sub">
            A dashboard is a grid of metric charts and log lists that all follow one clock. Make
            your first one and pin the tiles you check every day.
          </p>
          <LgButton variant="primary" @click="newOpen = true">
            <template #icon><Plus :size="14" aria-hidden="true" /></template>
            New dashboard
          </LgButton>
        </div>

        <ul v-else class="dash-list">
          <li v-for="d in list" :key="d.id" class="dash-row">
            <button type="button" class="dash-open" @click="openDashboard(d.id)">
              <LayoutDashboard :size="16" aria-hidden="true" class="dash-ico" />
              <span class="dash-name">{{ d.name }}</span>
              <span class="dash-meta">{{ d.owner }} · updated {{ relTime(d.updatedAt) }}</span>
            </button>
            <LgIconButton
              :label="homeId === d.id ? 'Unset as home dashboard' : 'Set as home dashboard'"
              size="sm"
              :active="homeId === d.id"
              :pressed="homeId === d.id"
              @click="toggleHome(d.id)"
            >
              <Star
                :size="15"
                aria-hidden="true"
                :fill="homeId === d.id ? 'currentColor' : 'none'"
              />
            </LgIconButton>
          </li>
        </ul>
        <p v-if="list.length" class="picker-hint">
          <Star :size="12" aria-hidden="true" /> marks your home dashboard — it greets you at
          sign-in.
        </p>
      </div>
    </template>

    <!-- ============ Viewing a dashboard ============ -->
    <template v-else>
      <div class="toolbar">
        <LgIconButton label="Back to dashboards" size="sm" @click="closeDashboard">
          <ArrowLeft :size="16" aria-hidden="true" />
        </LgIconButton>
        <h2 class="dash-title" :title="current.name">{{ current.name }}</h2>

        <div class="toolbar-spacer" />

        <LgButton
          size="sm"
          variant="ghost"
          :disabled="tiles.length >= MAX_TILES"
          @click="addOpen = true"
        >
          <template #icon><Plus :size="14" aria-hidden="true" /></template>
          Add tile
        </LgButton>

        <LgButton
          size="sm"
          variant="ghost"
          :aria-pressed="editing"
          :class="{ 'edit-on': editing }"
          @click="editing = !editing"
        >
          <template #icon><Pencil :size="14" aria-hidden="true" /></template>
          {{ editing ? 'Done' : 'Edit' }}
        </LgButton>

        <LgMenu :items="dashMenuItems" align="right" @select="onMenuSelect">
          <template #trigger>
            <LgIconButton label="Dashboard actions" size="sm">
              <EllipsisVertical :size="16" aria-hidden="true" />
            </LgIconButton>
          </template>
        </LgMenu>

        <!-- The dashboard clock (§3.4): ONE range + ONE live toggle for all tiles -->
        <TimeRangePicker :model-value="clock.range.value" @update:model-value="clock.setRange" />
        <LgTooltip :text="liveTitle" placement="bottom">
          <LgButton
            class="live-toggle"
            :class="clock.live.value ? 'live' : 'off'"
            size="sm"
            variant="ghost"
            :aria-pressed="clock.live.value"
            @click="clock.live.value = !clock.live.value"
          >
            <template #icon>
              <Play v-if="clock.live.value" :size="14" aria-hidden="true" />
              <Pause v-else :size="14" aria-hidden="true" />
            </template>
            {{ clock.live.value ? 'Live' : 'Paused' }}
          </LgButton>
        </LgTooltip>
      </div>

      <p v-if="saveError" class="save-note" role="alert">
        {{ saveError }}
        <button class="link-btn" @click="saveNow">Retry</button>
      </p>

      <div class="dash-body">
        <div v-if="dashLoading" class="picker-state"><p>Loading dashboard…</p></div>
        <DashboardGrid
          v-else
          :tiles="tiles"
          :from="clock.from.value"
          :to="clock.to.value"
          :live="clock.live.value"
          :editing="editing"
          @move="onMove"
          @resize="onResize"
          @remove="onRemove"
          @unauthorized="$emit('unauthorized')"
        />
      </div>
    </template>

    <!-- New dashboard -->
    <LgModal :open="newOpen" title="New dashboard" @close="newOpen = false">
      <form class="modal-form" @submit.prevent="createDashboard">
        <label class="modal-field">
          <span class="modal-label">Name</span>
          <LgInput
            :model-value="newName"
            placeholder="e.g. Production at a glance"
            aria-label="Dashboard name"
            @update:model-value="newName = $event"
          />
        </label>
        <p v-if="newError" class="form-error" role="alert">{{ newError }}</p>
      </form>
      <template #footer>
        <LgButton variant="ghost" @click="newOpen = false">Cancel</LgButton>
        <LgButton
          variant="primary"
          :disabled="!newName.trim() || creating"
          @click="createDashboard"
        >
          {{ creating ? 'Creating…' : 'Create' }}
        </LgButton>
      </template>
    </LgModal>

    <!-- Rename -->
    <LgModal :open="renameOpen" title="Rename dashboard" @close="renameOpen = false">
      <form class="modal-form" @submit.prevent="renameDashboard">
        <label class="modal-field">
          <span class="modal-label">Name</span>
          <LgInput
            :model-value="renameName"
            aria-label="Dashboard name"
            @update:model-value="renameName = $event"
          />
        </label>
      </form>
      <template #footer>
        <LgButton variant="ghost" @click="renameOpen = false">Cancel</LgButton>
        <LgButton variant="primary" :disabled="!renameName.trim()" @click="renameDashboard">
          Rename
        </LgButton>
      </template>
    </LgModal>

    <!-- Delete confirm -->
    <LgModal :open="deleteOpen" title="Delete dashboard" @close="deleteOpen = false">
      <p class="confirm-text">
        Delete <strong>{{ current?.name }}</strong> for everyone? Its tiles are gone for good (the
        saved explorations and filter sets they reference are kept).
      </p>
      <template #footer>
        <LgButton variant="ghost" @click="deleteOpen = false">Cancel</LgButton>
        <LgButton variant="danger" @click="deleteDashboard">Delete</LgButton>
      </template>
    </LgModal>

    <!-- Add tile -->
    <AddTileModal :open="addOpen" @close="addOpen = false" @add="onAddTile" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import {
  ArrowLeft,
  EllipsisVertical,
  LayoutDashboard,
  Pause,
  Pencil,
  Play,
  Plus,
  Star,
  Trash2,
  TextCursorInput,
} from 'lucide-vue-next';
import {
  api,
  ApiError,
  type DashboardSummary,
  type DashboardTimeRange,
  type ResolvedTile,
} from '../api.js';
import { DEFAULT_RANGE } from '../composables/useFilters.js';
import { useDashboardClock } from '../composables/useDashboardClock.js';
import {
  MAX_TILES,
  moveTile,
  normalizeTiles,
  rawLayout,
  removeTile,
  resizeTile,
  type SizeStep,
} from '../lib/dashboard-layout.js';
import { formatRelativeTime } from '../lib/format-metric.js';
import { SCRAPE_INTERVAL_MS } from '../lib/metric-exploration.js';
import type { MenuItem } from '../ui/LgMenu.vue';
import DashboardGrid from '../components/dashboards/DashboardGrid.vue';
import AddTileModal from '../components/dashboards/AddTileModal.vue';
import TimeRangePicker from '../components/TimeRangePicker.vue';
import GoblinIcon from '../components/GoblinIcon.vue';
import LgButton from '../ui/LgButton.vue';
import LgIconButton from '../ui/LgIconButton.vue';
import LgInput from '../ui/LgInput.vue';
import LgModal from '../ui/LgModal.vue';
import LgMenu from '../ui/LgMenu.vue';
import LgTooltip from '../ui/LgTooltip.vue';

const emit = defineEmits<{ unauthorized: [] }>();

// --- List (picker) ----------------------------------------------------------
const list = ref<DashboardSummary[]>([]);
const listLoading = ref(false);
const listError = ref(false);
const homeId = ref<number | null>(null);

// --- Current dashboard --------------------------------------------------------
interface CurrentDash {
  id: number;
  name: string;
  timeDefault: DashboardTimeRange;
}
const current = ref<CurrentDash | null>(null);
const tiles = ref<ResolvedTile[]>([]);
const dashLoading = ref(false);
const editing = ref(false);

const clock = useDashboardClock();
const liveTitle = computed(() =>
  clock.live.value
    ? `Every tile refreshes every ${SCRAPE_INTERVAL_MS / 1000}s. Click to pause.`
    : 'The window is frozen. Click to resume.',
);

// --- Modals -------------------------------------------------------------------
const newOpen = ref(false);
const newName = ref('');
const newError = ref('');
const creating = ref(false);
const renameOpen = ref(false);
const renameName = ref('');
const deleteOpen = ref(false);
const addOpen = ref(false);

const dashMenuItems = computed<MenuItem[]>(() => [
  { label: 'Rename', value: 'rename', icon: TextCursorInput },
  {
    label: current.value && homeId.value === current.value.id ? 'Unset as home' : 'Set as home',
    value: 'home',
    icon: Star,
  },
  { label: 'Delete', value: 'delete', icon: Trash2, danger: true },
]);

function onUnauthorized(e: unknown) {
  if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
}

function relTime(ts: number): string {
  return formatRelativeTime(ts);
}

// --- List loading ---------------------------------------------------------------
async function loadList() {
  listLoading.value = true;
  try {
    const [rows, home] = await Promise.all([api.dashboards.list(), api.getHomeDashboardId()]);
    list.value = rows;
    homeId.value = home;
    listError.value = false;
  } catch (e) {
    listError.value = true;
    onUnauthorized(e);
  } finally {
    listLoading.value = false;
  }
}

async function toggleHome(id: number) {
  const previous = homeId.value;
  const next = previous === id ? null : id;
  homeId.value = next; // optimistic
  try {
    await api.putHomeDashboardId(next);
  } catch (e) {
    homeId.value = previous; // roll back
    onUnauthorized(e);
  }
}

// --- Open / close ---------------------------------------------------------------
async function openDashboard(id: number) {
  dashLoading.value = true;
  editing.value = false;
  try {
    const dash = await api.dashboards.resolved(id);
    current.value = { id: dash.id, name: dash.name, timeDefault: dash.timeDefault };
    tiles.value = normalizeTiles(dash.layout.tiles);
    clock.reset(dash.timeDefault);
  } catch (e) {
    current.value = null;
    onUnauthorized(e);
  } finally {
    dashLoading.value = false;
  }
}

async function closeDashboard() {
  await flushSave();
  current.value = null;
  tiles.value = [];
  editing.value = false;
  loadList();
}

// --- Persistence (debounced PUT so edits never sit unsaved for long) --------------
const saveError = ref('');
let saveTimer: ReturnType<typeof setTimeout> | undefined;
let dirty = false;

function scheduleSave() {
  dirty = true;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(saveNow, 800);
}

async function saveNow() {
  const dash = current.value;
  if (!dash || !dirty) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = undefined;
  dirty = false;
  try {
    await api.dashboards.update(dash.id, {
      name: dash.name,
      layout: rawLayout(tiles.value),
      // The session range is an override (§3.4); the stored default is kept.
      timeDefault: dash.timeDefault,
    });
    saveError.value = '';
  } catch (e) {
    dirty = true; // keep the edit pending so Retry / the next change re-sends it
    saveError.value = 'Couldn’t save your changes.';
    onUnauthorized(e);
  }
}

async function flushSave() {
  if (dirty) await saveNow();
}

// --- Create / rename / delete ------------------------------------------------------
async function createDashboard() {
  const name = newName.value.trim();
  if (!name || creating.value) return;
  creating.value = true;
  newError.value = '';
  try {
    const { id } = await api.dashboards.create({
      name,
      layout: { tiles: [] },
      timeDefault: DEFAULT_RANGE,
    });
    newOpen.value = false;
    newName.value = '';
    await openDashboard(id);
    // A fresh dashboard is empty — drop straight into building it.
    editing.value = true;
    addOpen.value = true;
  } catch (e) {
    newError.value = e instanceof ApiError ? e.message : 'Couldn’t create the dashboard.';
    onUnauthorized(e);
  } finally {
    creating.value = false;
  }
}

function renameDashboard() {
  const name = renameName.value.trim();
  if (!name || !current.value) return;
  current.value.name = name;
  renameOpen.value = false;
  scheduleSave();
}

async function deleteDashboard() {
  const dash = current.value;
  if (!dash) return;
  deleteOpen.value = false;
  // Discard any pending layout save — the dashboard is going away.
  dirty = false;
  if (saveTimer) clearTimeout(saveTimer);
  try {
    await api.dashboards.delete(dash.id);
    if (homeId.value === dash.id) homeId.value = null; // server prunes the setting on read
    current.value = null;
    tiles.value = [];
    loadList();
  } catch (e) {
    saveError.value = 'Couldn’t delete the dashboard.';
    onUnauthorized(e);
  }
}

function onMenuSelect(value: string) {
  if (value === 'rename') {
    renameName.value = current.value?.name ?? '';
    renameOpen.value = true;
  } else if (value === 'home') {
    if (current.value) toggleHome(current.value.id);
  } else if (value === 'delete') {
    deleteOpen.value = true;
  }
}

// --- Tile edits -----------------------------------------------------------------
function onMove(id: string, dir: 'up' | 'down') {
  tiles.value = moveTile(tiles.value, id, dir);
  scheduleSave();
}
function onResize(id: string, size: SizeStep) {
  tiles.value = resizeTile(tiles.value, id, size);
  scheduleSave();
}
function onRemove(id: string) {
  tiles.value = removeTile(tiles.value, id);
  scheduleSave();
}
function onAddTile(tile: ResolvedTile) {
  if (tiles.value.length >= MAX_TILES) return;
  tiles.value = normalizeTiles([...tiles.value, tile]);
  addOpen.value = false;
  scheduleSave();
}

onMounted(loadList);
onBeforeUnmount(() => {
  // Don't lose a pending edit on navigation away.
  flushSave();
});
</script>

<style scoped>
.dashboards {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.toolbar-spacer {
  flex: 1;
  min-width: 0;
}
.picker-title,
.dash-title {
  margin: 0;
  font-size: var(--lg-fs-md);
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-toggle.live {
  color: var(--lg-ok);
}
.live-toggle.off {
  color: var(--lg-text-faint);
}
.edit-on {
  color: var(--lg-accent);
}

.picker-body,
.dash-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--lg-space-3);
}
.picker-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-8) var(--lg-space-4);
  color: var(--lg-text-faint);
  text-align: center;
}
.state-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.state-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 52ch;
}

.dash-list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.dash-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-lg);
  background: var(--lg-bg-surface);
  padding: var(--lg-space-2) var(--lg-space-3);
  min-width: 0;
}
.dash-open {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  font: inherit;
  text-align: left;
  background: none;
  border: none;
  color: var(--lg-text);
  cursor: pointer;
  padding: var(--lg-space-1) 0;
}
.dash-ico {
  color: var(--lg-text-faint);
  flex-shrink: 0;
}
.dash-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.dash-meta {
  margin-left: auto;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  flex-shrink: 0;
}
.dash-open:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
  border-radius: var(--lg-radius-sm);
}
.picker-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-1);
  margin: var(--lg-space-3) 0 0;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}

.save-note {
  margin: 0;
  padding: var(--lg-space-1) var(--lg-space-3);
  font-size: var(--lg-fs-xs);
  color: var(--lg-warn);
  border-bottom: 1px solid var(--lg-border-faint);
  flex-shrink: 0;
}
.link-btn {
  font: inherit;
  background: none;
  border: none;
  color: var(--lg-accent);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.modal-field {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.modal-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.form-error {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-danger);
}
.confirm-text {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}

@media (max-width: 768px) {
  .picker-body,
  .dash-body {
    padding: var(--lg-space-2);
  }
  .dash-meta {
    display: none;
  }
}
</style>
